import React, { useState, useEffect, useMemo } from 'react';
import { Table, Reservation, Zone, FloorDimensions, RestaurantConfig, CategoryDefinition, User, UserRole, PackageBooking } from '../types';
import { Trophy, Calendar, Clock, Users, CheckCircle, Plus, Minus, Activity, Target, Zap, Dumbbell, MapPin, ArrowRight, UserCheck, Hourglass, Volleyball, Star, ChevronRight, X, ShieldCheck, Flame, Play, Mail, Shield, Navigation, AlertCircle, Timer, MessageSquare, Phone, Headset, Instagram, Contact, Globe, Facebook, Twitter, Music2, Sparkles, Gift, ChevronDown, ChevronUp, LayoutGrid, RotateCcw, Pencil, Trash2, Repeat, User as UserIcon, Info, KeyRound, Send, FileText, Lock } from 'lucide-react';

interface CustomerViewProps {
  tables: Table[];
  reservations: Reservation[];
  zones: Zone[];
  onMakeReservation: (reservation: Reservation) => void;
  onCancelReservation: (reservationId: string) => void;
  dimensions: FloorDimensions;
  config: RestaurantConfig;
  categories: CategoryDefinition[];
  currentUser: User | null;
  isGuestMode: boolean;
  setIsGuestMode: (val: boolean) => void;
  onLogin: (user: User) => void;
  onLoginRequest: () => void; 
}

const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const FacebookLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" />
  </svg>
);

export const CustomerView: React.FC<CustomerViewProps> = ({ 
  tables, 
  reservations, 
  zones, 
  onMakeReservation, 
  onCancelReservation,
  dimensions, 
  config, 
  categories,
  currentUser,
  isGuestMode,
  setIsGuestMode,
  onLogin,
  onLoginRequest
}) => {
  const [viewMode, setViewMode] = useState<'BOOK' | 'MY_BOOKINGS'>('BOOK');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [isPackagesExpanded, setIsPackagesExpanded] = useState(false); 
  const [showPackageInfoId, setShowPackageInfoId] = useState<string | null>(null);
  
  // Modals State
  const [showImprint, setShowImprint] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginName, setLoginName] = useState('');

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [bookingResults, setBookingResults] = useState<{ success: number; failed: string[] } | null>(null);

  const [formErrors, setFormErrors] = useState<{name?: boolean; email?: boolean; phone?: boolean}>({});

  const availableCategories = useMemo(() => {
    return categories.filter(cat => 
        tables.some(table => table.category === cat.id)
    );
  }, [categories, tables]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '', 
    partySize: 6,
    duration: 60,
    categoryId: '',
    notes: ''
  });

  const selectedCategory = useMemo(() => 
    categories.find(c => c.id === formData.categoryId), 
    [categories, formData.categoryId]
  );

  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, startTime: '' }));
  }, [formData.duration, formData.categoryId, formData.date]);

  useEffect(() => {
    if (currentUser && !formData.name) {
      setFormData(prev => ({ ...prev, name: currentUser.name, email: currentUser.email }));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!formData.categoryId && availableCategories.length > 0 && !selectedPackageId) {
      const firstCat = availableCategories[0];
      setFormData(prev => ({ 
        ...prev, 
        categoryId: firstCat.id,
        duration: firstCat.minDuration || 60 
      }));
    }
  }, [availableCategories, selectedPackageId]);

  const handlePackageSelect = (pkg: PackageBooking) => {
    if (selectedPackageId === pkg.id) {
        handleResetPackage();
        return;
    }
    setSelectedPackageId(pkg.id);
    setFormData(prev => ({
      ...prev,
      categoryId: pkg.categoryId,
      duration: pkg.durationMinutes,
      partySize: pkg.defaultPartySize,
      notes: `Gebucht: ${pkg.name}.`
    }));
  };

  const handleResetPackage = () => {
    setSelectedPackageId(null);
    const firstCat = availableCategories.find(c => c.id === formData.categoryId) || availableCategories[0];
    setFormData(prev => ({ 
      ...prev, 
      notes: '',
      duration: Math.max(prev.duration, firstCat?.minDuration || 60)
    }));
    setIsPackagesExpanded(false); 
    setIsRecurring(false);
    setFormErrors({});
  };

  const calculatedTimeRange = useMemo(() => {
    if (!formData.startTime) return null;
    const [h, m] = formData.startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(h, m + formData.duration, 0, 0);
    const endTime = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return `${formData.startTime} - ${endTime}`;
  }, [formData.startTime, formData.duration]);

  const timeSlots = useMemo(() => {
    if (!formData.categoryId) return [];
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const selectedDateObj = new Date(formData.date);
    const selectedDayName = dayNames[selectedDateObj.getDay()];
    
    const activePackage = config.packages?.find(p => p.id === selectedPackageId);
    if (activePackage) {
      if (!activePackage.allowedDays.includes(selectedDayName)) return [];
    }

    const daySched = config.schedule[selectedDayName] || { open: '08:00', close: '22:00', isClosed: false };
    if (daySched.isClosed) return [];

    let openLimit = daySched.open;
    let closeLimit = daySched.close;

    if (activePackage) {
      if (activePackage.startTime > openLimit) openLimit = activePackage.startTime;
      if (activePackage.endTime < closeLimit) closeLimit = daySched.close;
    }

    const slots = [];
    const [openH, openM] = openLimit.split(':').map(Number);
    const [closeH, closeM] = closeLimit.split(':').map(Number);
    
    const calendarStart = new Date(selectedDateObj);
    calendarStart.setHours(openH, openM, 0, 0);
    
    const calendarEndLimit = new Date(selectedDateObj);
    calendarEndLimit.setHours(closeH, closeM, 0, 0);

    const now = new Date();
    const isToday = formData.date === now.toISOString().split('T')[0];
    const candidates = tables.filter(t => t.category === formData.categoryId);

    const tempTime = new Date(calendarStart);
    while (tempTime < calendarEndLimit) {
      const timeStr = tempTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      const requestedDuration = formData.duration;
      const slotStartMs = tempTime.getTime();
      const slotEndMs = slotStartMs + requestedDuration * 60000;
      const isPast = isToday && tempTime < now;
      const fitsInSchedule = slotEndMs <= calendarEndLimit.getTime();

      let hasAvailability = false;
      if (fitsInSchedule && !isPast) {
        hasAvailability = candidates.some(t => {
          const hasConflict = reservations.some(res => {
            if (res.tableId !== t.id || res.date !== formData.date || res.status === 'CANCELLED_BY_GUEST') return false;
            const resStart = new Date(`${res.date}T${res.time}`).getTime();
            const resEnd = resStart + res.durationMinutes * 60000;
            return slotStartMs < resEnd && slotEndMs > resStart;
          });
          return !hasConflict;
        });
      }

      slots.push({
        time: timeStr,
        isAvailable: fitsInSchedule && !isPast && hasAvailability,
        isPast,
        outOfBounds: !fitsInSchedule
      });

      tempTime.setMinutes(tempTime.getMinutes() + 30);
    }

    return slots;
  }, [formData.date, formData.categoryId, formData.duration, config.schedule, tables, reservations, selectedPackageId, config.packages]);

  const handleAuthSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginWithEmail(loginEmail, loginName);
  };

  const handlePasswordResetSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simuliere API Aufruf
    setTimeout(() => {
        setIsProcessing(false);
        setPasswordResetSent(true);
    }, 1500);
  };

  const loginWithEmail = (emailVal: string, nameVal: string) => {
    if (emailVal.toLowerCase().includes('admin')) {
        const adminUser: User = {
            id: 'admin-1',
            name: nameVal || 'Administrator',
            email: emailVal,
            role: UserRole.ADMIN
        };
        onLogin(adminUser);
    } else {
        const guestUser: User = {
            id: emailVal,
            name: nameVal || emailVal.split('@')[0],
            email: emailVal,
            role: UserRole.GUEST
        };
        onLogin(guestUser);
    }
  };

  const handleDirectBooking = () => {
    if (!formData.startTime) return;
    
    if (!currentUser && isGuestMode) {
      const errors: {name?: boolean; email?: boolean; phone?: boolean} = {};
      if (!formData.name.trim()) errors.name = true;
      if (!formData.email.trim()) errors.email = true;
      if (!formData.phone.trim()) errors.phone = true;

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }

    setFormErrors({});
    setIsProcessing(true);
    
    const duration = formData.duration;
    const candidates = tables.filter(t => t.category === formData.categoryId).sort((a,b) => a.seats - b.seats);
    
    const datesToBook = [formData.date];
    if (isRecurring && recurringEndDate) {
        let current = new Date(formData.date);
        const endLimit = new Date(recurringEndDate);
        
        while(true) {
            current.setDate(current.getDate() + 7);
            if (current > endLimit) break;
            datesToBook.push(current.toISOString().split('T')[0]);
        }
    }

    const seriesId = isRecurring ? Math.random().toString(36).substr(2, 9) : undefined;
    let successCount = 0;
    const failedDates: string[] = [];

    datesToBook.forEach(date => {
        const reqStart = new Date(`${date}T${formData.startTime}`).getTime();
        const reqEnd = reqStart + duration * 60000;

        const availableTable = candidates.find(t => {
            return !reservations.some(res => {
                if (res.tableId !== t.id || res.date !== date || res.status === 'CANCELLED_BY_GUEST') return false;
                const resStart = new Date(`${res.date}T${res.time}`).getTime();
                const resEnd = resStart + res.durationMinutes * 60000;
                return reqStart < resEnd && reqEnd > resStart;
            });
        });

        if (availableTable) {
            onMakeReservation({
                id: Math.random().toString(36).substr(2, 9),
                tableId: availableTable.id,
                categoryId: formData.categoryId,
                packageName: selectedPackageId ? config.packages?.find(p => p.id === selectedPackageId)?.name : undefined,
                userId: currentUser?.id,
                customerName: formData.name || 'Gast',
                phone: formData.phone,
                email: formData.email,
                partySize: formData.partySize,
                date: date,
                time: formData.startTime,
                durationMinutes: duration,
                notes: formData.notes,
                status: (isRecurring ? config.recurringRequireApproval : config.requireReservationApproval) ? 'PENDING' : 'CONFIRMED',
                seriesId
            });
            successCount++;
        } else {
            failedDates.push(new Date(date).toLocaleDateString('de-DE'));
        }
    });

    setIsProcessing(false);
    setBookingResults({ success: successCount, failed: failedDates });
    setStep(3);
  };

  const getCategoryIcon = (iconName: string) => {
    const size = 24;
    switch(iconName) {
        case 'trophy': return <Trophy size={size} />;
        case 'target': return <Target size={size} />;
        case 'users': return <Users size={size} />;
        case 'activity': return <Activity size={size} />;
        case 'zap': return <Zap size={size} />;
        case 'dumbbell': return <Dumbbell size={size} />;
        case 'volleyball': return <Volleyball size={size} />;
        default: return <Trophy size={size} />;
    }
  };

  const QuickButton = ({ label, role, icon: Icon, email }: { label: string, role: string, icon: any, email: string }) => (
    <button
      onClick={() => loginWithEmail(email, label)}
      className="flex-1 flex flex-col items-center justify-center p-3 border border-white/10 rounded-2xl hover:bg-white/10 transition-all hover:scale-105 active:scale-95 group bg-white/5"
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 transition-colors ${role === 'Admin' ? 'bg-amber-500/20 text-amber-500' : 'bg-emerald-500/20 text-emerald-500'}`}>
        <Icon size={16} />
      </div>
      <span className="text-sm font-black text-white truncate w-full">{label}</span>
      <span className="text-[8px] text-slate-500 uppercase tracking-tighter">{role}</span>
    </button>
  );

  const secondaryColor = config.theme?.secondaryColor || '#064e3b';
  const primaryColor = config.theme?.primaryColor || '#10b981';

  const groupedUserReservations = useMemo(() => {
      const now = new Date().getTime();
      // Filter out CANCELLED_BY_GUEST so the user doesn't see them as active/upcoming
      const userRes = reservations.filter(r => (r.userId === currentUser?.id || (!currentUser && isGuestMode && (r.email === formData.email || r.phone === formData.phone))) && r.status !== 'CANCELLED_BY_GUEST'); 
      
      const pending: Reservation[] = [];
      const upcoming: Reservation[] = [];
      const past: Reservation[] = [];

      userRes.forEach(r => {
          const resStart = new Date(`${r.date}T${r.time}`).getTime();
          const resEnd = resStart + r.durationMinutes * 60000;

          if (r.status === 'PENDING') {
              pending.push(r);
          } else if (resEnd < now) {
              past.push(r);
          } else {
              upcoming.push(r);
          }
      });

      const sortFn = (a: Reservation, b: Reservation) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime();
      const sortFnDesc = (a: Reservation, b: Reservation) => new Date(`${b.date}T${b.time}`).getTime() - new Date(`${a.date}T${a.time}`).getTime();

      return {
          pending: pending.sort(sortFn),
          upcoming: upcoming.sort(sortFn),
          past: past.sort(sortFnDesc)
      };
  }, [reservations, currentUser, isGuestMode, formData.email, formData.phone]);

  const handleEditReservation = (res: Reservation) => {
      setFormData({
          name: res.customerName,
          email: res.email || '',
          phone: res.phone || '',
          date: res.date,
          startTime: res.time,
          partySize: res.partySize,
          duration: res.durationMinutes,
          categoryId: res.categoryId,
          notes: res.notes || ''
      });
      const pkg = config.packages?.find(p => p.name === res.packageName);
      if (pkg) {
          setSelectedPackageId(pkg.id);
      } else {
          setSelectedPackageId(null);
      }
      onCancelReservation(res.id);
      setViewMode('BOOK');
      setStep(1);
  };

  const googleMapsUrl = useMemo(() => {
      if (!config.address) return '#';
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.address)}`;
  }, [config.address]);

  if (!currentUser && !isGuestMode) {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const germanDays: Record<string, string> = {
        Monday: 'Mo', Tuesday: 'Di', Wednesday: 'Mi', Thursday: 'Do', Friday: 'Fr', Saturday: 'Sa', Sunday: 'So'
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 relative overflow-hidden font-sans">
            <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[120px] animate-pulse delay-700"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 pointer-events-none"></div>

            <div className="w-full max-w-7xl z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start lg:items-center py-10 flex-grow">
                <div className="lg:col-span-7 order-1 animate-fade-in-down space-y-6">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full mb-6">
                             <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_8px_#10b981]"></span>
                             <span className="text-[10px] font-black text-white uppercase tracking-widest">{config.name} Live</span>
                        </div>
                        
                        {config.logoUrl ? (
                          <img src={config.logoUrl} alt={config.name} className="h-24 md:h-32 w-auto object-contain mb-8 animate-fade-in" />
                        ) : (
                          <h1 className="text-6xl md:text-8xl font-black text-white italic tracking-tighter mb-4 leading-[0.85] pr-4 uppercase">
                              {config.name}
                          </h1>
                        )}
                        
                        <p className="text-xl text-slate-500 font-medium max-w-md lg:max-w-none">
                            {config.welcomeText || 'Dein Court wartet. Melde dich an und sichere dir deinen Slot.'}
                        </p>
                    </div>
                </div>

                <div className="lg:col-span-5 lg:row-span-2 order-2 bg-white/5 backdrop-blur-3xl border border-white/10 rounded-[48px] p-10 md:p-12 shadow-2xl relative min-h-[500px] flex flex-col">
                    {isResettingPassword ? (
                        <div className="animate-fade-in flex flex-col h-full flex-1">
                            <h2 className="text-3xl font-black text-white mb-2 tracking-tight">Passwort Reset.</h2>
                            <p className="text-slate-400 text-sm mb-8">Gib deine E-Mail an, um Anweisungen zu erhalten.</p>
                            
                            {passwordResetSent ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-in py-10">
                                    <div className="w-20 h-20 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mb-6 border border-emerald-500/30">
                                        <Send size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2 tracking-tight">Email gesendet!</h3>
                                    <p className="text-slate-500 text-sm mb-10 max-w-[240px]">Prüfe deinen Posteingang für den Wiederherstellungs-Link.</p>
                                    <button 
                                        onClick={() => { setIsResettingPassword(false); setPasswordResetSent(false); }}
                                        className="text-emerald-400 text-xs font-black uppercase tracking-widest hover:text-emerald-300 transition-colors"
                                    >
                                        Zurück zum Login
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handlePasswordResetSubmit} className="space-y-6 flex-1">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-500 uppercase ml-2">Email</label>
                                        <input 
                                            type="email" 
                                            required
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold"
                                            placeholder="name@sport.de"
                                            value={loginEmail}
                                            onChange={e => setLoginEmail(e.target.value)}
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <button 
                                            type="submit"
                                            disabled={isProcessing}
                                            className="w-full py-5 bg-white text-slate-950 rounded-2xl font-black text-lg hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-white/5 flex items-center justify-center gap-3 disabled:opacity-50"
                                        >
                                            <KeyRound size={22} /> {isProcessing ? 'Sende...' : 'Link anfordern'}
                                        </button>
                                    </div>

                                    <div className="text-center">
                                        <button 
                                            type="button"
                                            onClick={() => setIsResettingPassword(false)}
                                            className="text-slate-500 text-xs font-black uppercase tracking-widest hover:text-emerald-300 transition-colors"
                                        >
                                            Abbrechen & Zurück
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    ) : (
                        <div className="animate-fade-in flex flex-col flex-1">
                            <h2 className="text-3xl font-black text-white mb-2 tracking-tight">Login.</h2>
                            <p className="text-slate-400 text-sm mb-6">Melde dich an für Member-Vorteile.</p>

                            {config.socialProviders && config.socialProviders.length > 0 && (
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    {config.socialProviders.includes('google') && (
                                        <button className="flex items-center justify-center gap-3 py-3 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all font-bold text-xs text-white group">
                                            <GoogleLogo /> <span className="opacity-70 group-hover:opacity-100 transition-opacity">Google</span>
                                        </button>
                                    )}
                                    {config.socialProviders.includes('facebook') && (
                                        <button className="flex items-center justify-center gap-3 py-3 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all font-bold text-xs text-white group">
                                            <FacebookLogo /> <span className="opacity-70 group-hover:opacity-100 transition-opacity">Facebook</span>
                                        </button>
                                    )}
                                </div>
                            )}

                            {(config.socialProviders && config.socialProviders.length > 0) && (
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="h-px flex-1 bg-white/10"></div>
                                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">oder</span>
                                    <div className="h-px flex-1 bg-white/10"></div>
                                </div>
                            )}

                            <form onSubmit={handleAuthSubmit} className="space-y-4 mb-8">
                                {isRegistering && (
                                    <div className="space-y-1 animate-fade-in">
                                        <label className="text-[10px] font-black text-slate-500 uppercase ml-2">Dein Name</label>
                                        <input 
                                            type="text" 
                                            required
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold"
                                            placeholder="Username"
                                            value={loginName}
                                            onChange={e => setLoginName(e.target.value)}
                                        />
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-500 uppercase ml-2">Email</label>
                                    <input 
                                        type="email" 
                                        required
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold"
                                        placeholder="name@sport.de"
                                        value={loginEmail}
                                        onChange={e => setLoginEmail(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-500 uppercase ml-2">Passwort</label>
                                    <input 
                                        type="password" 
                                        required 
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold"
                                        placeholder="••••••••"
                                        value={loginPassword}
                                        onChange={e => setLoginPassword(e.target.value)}
                                    />
                                </div>
                                
                                <div className="pt-2">
                                    <button 
                                        type="submit"
                                        className="w-full py-5 bg-white text-slate-950 rounded-2xl font-black text-lg hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-white/5 flex items-center justify-center gap-3"
                                    >
                                        <UserCheck size={22} /> {isRegistering ? 'Account erstellen' : 'Member Login'}
                                    </button>
                                </div>
                            </form>

                            <div className="flex flex-col gap-4 text-center">
                                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                                    <button 
                                        onClick={() => setIsRegistering(!isRegistering)}
                                        className="text-emerald-400 text-xs font-black uppercase tracking-widest hover:text-emerald-300 transition-colors"
                                    >
                                        {isRegistering ? 'Doch lieber einloggen?' : 'Noch kein Member? Jetzt registrieren'}
                                    </button>
                                    {!isRegistering && (
                                        <>
                                            <span className="w-1 h-1 bg-slate-700 rounded-full hidden sm:block"></span>
                                            <button 
                                                type="button" 
                                                onClick={() => setIsResettingPassword(true)}
                                                className="text-slate-500 text-xs font-black uppercase tracking-widest hover:text-emerald-300 transition-colors"
                                            >
                                                Passwort vergessen?
                                            </button>
                                        </>
                                    )}
                                </div>
                                
                                {config.allowGuestReservations && (
                                <button 
                                    onClick={() => setIsGuestMode(true)}
                                    className="w-full py-4 border-2 border-white/10 text-slate-400 rounded-2xl font-bold text-sm hover:bg-white/5 hover:text-white transition-all flex items-center justify-center gap-3"
                                >
                                    <Play size={16} fill="currentColor" /> Direkt als Gast buchen
                                </button>
                                )}

                                <div className="flex items-center gap-4 my-4">
                                    <div className="h-px flex-1 bg-white/10"></div>
                                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">oder</span>
                                    <div className="h-px flex-1 bg-white/10"></div>
                                </div>

                                <div className="flex gap-3">
                                    <QuickButton label="M. Mustermann" role="Gast" icon={Zap} email="max@gast.de" />
                                    <QuickButton label="Arena Chef" role="Admin" icon={Shield} email="admin@sportarena.de" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-7 order-3 space-y-8 mt-12 lg:mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                        <div className="bg-white/5 border border-white/10 rounded-[32px] p-8 backdrop-blur-md flex flex-col h-full">
                            <h3 className="text-xs font-black text-emerald-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <Clock size={16} /> Öffnungszeiten
                            </h3>
                            <div className="space-y-3 flex-grow">
                                {days.map(day => {
                                    const sched = config.schedule[day] || { open: '08:00', close: '22:00', isClosed: false };
                                    const isToday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date()) === day;
                                    return (
                                        <div key={day} className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${isToday ? 'text-white' : 'text-slate-50'}`}>
                                            <span className={`text-xs font-black uppercase ${isToday ? 'text-emerald-400 underline underline-offset-4' : ''}`}>{germanDays[day]}</span>
                                            <span className="text-lg font-bold">
                                                {sched.isClosed ? 'Geschlossen' : `${sched.open} - ${sched.close}`}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-[32px] p-8 backdrop-blur-md flex flex-col h-full">
                            <div className="flex-grow">
                                <h3 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                    <MapPin size={16} /> Standort
                                </h3>
                                <div className="text-2xl text-white font-black italic leading-tight mb-4 tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                                    {config.name}
                                </div>
                                <div className="text-slate-400 text-lg font-medium leading-tight space-y-0.5">
                                    {config.showOwnerOnStart && config.ownerName && (
                                        <div className="opacity-80">
                                            Inh.: {config.ownerName}
                                        </div>
                                    )}
                                    {config.address ? (
                                      config.address.split(',').map((part, i) => (
                                        <React.Fragment key={i}>
                                          {part.trim()}
                                          {i < config.address!.split(',').length - 1 && <br />}
                                        </React.Fragment>
                                      ))
                                    ) : (
                                      <>
                                        Sportallee 42<br />
                                        12345 Hauptstadt<br />
                                        Deutschland
                                      </>
                                    )}
                                </div>

                                {(config.phoneLandline || config.phoneMobile || config.contactEmail) && (
                                    <div className="space-y-3 mt-8 pt-4 border-t border-white/5">
                                        <h3 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                            <Contact size={16} /> Kontakt
                                        </h3>
                                        {config.phoneLandline && (
                                            <a href={`tel:${config.phoneLandline.replace(/\s+/g, '')}`} className="flex items-center gap-3 text-slate-300 text-sm font-bold hover:text-emerald-400 transition-colors">
                                                <Phone size={14} className="text-blue-400 shrink-0" />
                                                <span>{config.phoneLandline}</span>
                                            </a>
                                        )}
                                        {config.phoneMobile && (
                                            <a href={`tel:${config.phoneMobile.replace(/\s+/g, '')}`} className="flex items-center gap-3 text-slate-300 text-sm font-bold hover:text-emerald-400 transition-colors">
                                                <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                                                    <Phone size={14} className="text-emerald-400" />
                                                </div>
                                                <span>{config.phoneMobile}</span>
                                            </a>
                                        )}
                                        {config.contactEmail && (
                                            <a href={`mailto:${config.contactEmail}`} className="flex items-center gap-3 text-slate-300 text-sm font-bold hover:text-emerald-400 transition-colors">
                                                <Mail size={14} className="text-purple-400 shrink-0" />
                                                <span className="truncate">{config.contactEmail}</span>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                            <a 
                                href={googleMapsUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-full py-4 bg-white/5 hover:bg-white text-emerald-400 hover:text-slate-950 border border-emerald-500/20 rounded-2xl flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest transition-all group mt-8"
                            >
                                <Navigation size={16} /> Anfahrt planen
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <footer className="w-full max-w-7xl mx-auto py-10 px-8 mt-auto border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-8 z-10 text-slate-500">
                <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
                    <div className="flex items-center gap-6 text-[10px] font-black uppercase tracking-[0.2em]">
                        <button onClick={() => setShowImprint(true)} className="hover:text-emerald-400 transition-colors">Impressum</button>
                        <button onClick={() => setShowPrivacy(true)} className="hover:text-emerald-400 transition-colors">Datenschutz</button>
                    </div>
                    <div className="hidden md:block w-px h-4 bg-white/10"></div>
                    <span className="text-[10px] font-bold opacity-40 uppercase tracking-widest text-center md:text-left">
                        © {new Date().getFullYear()} {config.name} — All Rights Reserved.
                    </span>
                    <div className="hidden md:block w-px h-4 bg-white/10"></div>
                    <a href="https://www.stillstudio.de" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest transition-colors group">
                        <span className="text-slate-500">BY</span>
                        <span style={{ color: '#0f172a' }}>still</span>
                        <span style={{ color: '#fb7185' }}>studio.de</span>
                    </a>
                </div>
            </footer>

            {/* Imprint Modal */}
            {showImprint && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh]">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-black text-slate-800 uppercase tracking-widest flex items-center gap-2"><FileText size={18} className="text-emerald-500"/> Impressum</h3>
                            <button onClick={() => setShowImprint(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400"><X size={20} /></button>
                        </div>
                        <div className="p-8 overflow-y-auto text-slate-600 text-sm space-y-6">
                            <div>
                                <h4 className="font-bold text-slate-900 mb-1">Angaben gemäß § 5 TMG</h4>
                                <p>{config.name}<br/>{config.address}</p>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 mb-1">Vertreten durch</h4>
                                <p>{config.ownerName || 'Geschäftsführung'}</p>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 mb-1">Kontakt</h4>
                                <p>
                                    {config.phoneLandline && <>Telefon: {config.phoneLandline}<br/></>}
                                    {config.phoneMobile && <>Mobil: {config.phoneMobile}<br/></>}
                                    {config.contactEmail && <>E-Mail: {config.contactEmail}</>}
                                </p>
                            </div>
                            <div className="text-xs text-slate-400 pt-4 border-t border-slate-100">
                                <p>Haftungsausschluss: Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy Modal */}
            {showPrivacy && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh]">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-black text-slate-800 uppercase tracking-widest flex items-center gap-2"><Lock size={18} className="text-emerald-500"/> Datenschutz (DSGVO)</h3>
                            <button onClick={() => setShowPrivacy(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400"><X size={20} /></button>
                        </div>
                        <div className="p-8 overflow-y-auto text-slate-600 text-sm space-y-6">
                            <div>
                                <h4 className="font-bold text-slate-900 mb-2 underline">1. Datenschutz auf einen Blick</h4>
                                <p className="mb-2"><strong>Allgemeine Hinweise:</strong> Die folgenden Hinweise geben einen einfachen Überblick darüber, what with your personal data happens if you use this app. Personal data is any data that allows you to be identified personally.</p>
                                <p><strong>Purpose-based processing:</strong> In this web app, personal data is processed exclusively for the purpose of booking appointments, reserving spaces, and user management. No other use of data occurs.</p>
                            </div>
                            
                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                <h4 className="font-bold text-slate-900 mb-2 italic">Additional information about your privacy:</h4>
                                <ul className="list-disc list-inside space-y-1 text-xs text-slate-700">
                                    <li>There are <strong>no third-party services</strong> (like ad networks) connected.</li>
                                    <li>There is <strong>no tracking or analysis</strong> of user behavior (Analytics).</li>
                                    <li>Your data will not be sold or shared with third parties for marketing purposes.</li>
                                </ul>
                            </div>

                            <div>
                                <h4 className="font-bold text-slate-900 mb-2 underline">2. Data collection in this app</h4>
                                <p><strong>Who is responsible for data collection?</strong> Responsible within the meaning of the GDPR is the operator of this app (see Imprint).</p>
                                <p className="mt-2"><strong>How do we collect your data?</strong> Your data is collected when you provide it to us. This can be, for example, the data you enter in the booking or registration form (name, email address, phone number).</p>
                                <p className="mt-2"><strong>What do we use your data for?</strong> Data processing is based on Art. 6 Para. 1 lit. b GDPR, which allows data processing for the fulfillment of a contract or pre-contractual measures.</p>
                            </div>

                            <div>
                                <h4 className="font-bold text-slate-900 mb-2 underline">3. Storage duration</h4>
                                <p>We store your data locally in the browser (Local Storage) and, if applicable, in our database for as long as it is necessary to provide our services (reservations, customer account) or as required by law.</p>
                            </div>

                            <div>
                                <h4 className="font-bold text-slate-900 mb-2 underline">4. Your rights (data subject rights)</h4>
                                <p>You have the following rights at any time under the applicable legal provisions:</p>
                                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                                    <li><strong>Access (Art. 15 GDPR):</strong> Right to information about your stored data.</li>
                                    <li><strong>Rectification (Art. 16 GDPR):</strong> Right to correct inaccurate data.</li>
                                    <li><strong>Erasure (Art. 17 GDPR):</strong> Right to delete your data ("Right to be forgotten").</li>
                                    <li><strong>Restriction (Art. 18 GDPR):</strong> Right to limit processing.</li>
                                    <li><strong>Portability (Art. 20 GDPR):</strong> Right to receive your data in a common format.</li>
                                    <li><strong>Withdrawal (Art. 7 Para. 3 GDPR):</strong> Right to withdraw consent at any time.</li>
                                </ul>
                                <p className="mt-2">To exercise these rights, please contact the address provided in the imprint.</p>
                            </div>

                            <div>
                                <h4 className="font-bold text-slate-900 mb-2 underline">5. Right to lodge a complaint</h4>
                                <p>In the event of violations of the GDPR, you have the right to lodge a complaint with the competent supervisory authority.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-2 md:p-6 animate-fade-in min-h-[80vh] flex flex-col">
        {showPackageInfoId && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-md p-6 animate-fade-in">
                <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]">
                    <div className="p-8 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center text-xl">
                                {config.packages?.find(p => p.id === showPackageInfoId)?.icon}
                            </div>
                            <h3 className="font-black text-xl uppercase tracking-tighter italic">
                                {config.packages?.find(p => p.id === showPackageInfoId)?.name}
                            </h3>
                        </div>
                        <button onClick={() => setShowPackageInfoId(null)} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
                            <X size={24} />
                        </button>
                    </div>
                    <div className="p-8 overflow-y-auto prose prose-emerald max-w-none prose-sm">
                        <div dangerouslySetInnerHTML={{ __html: config.packages?.find(p => p.id === showPackageInfoId)?.extendedInfo || '<p class="text-slate-400 italic">Keine weiteren Details verfügbar.</p>' }} />
                    </div>
                    <div className="p-8 bg-slate-50 border-t border-slate-100">
                        <button onClick={() => setShowPackageInfoId(null)} className="w-full py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-xl shadow-emerald-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all uppercase tracking-widest">Schließen</button>
                    </div>
                </div>
            </div>
        )}

        <div className="bg-white rounded-3xl md:rounded-[40px] shadow-2xl overflow-hidden border border-slate-100 mb-12">
            <div className="flex p-1.5 md:p-2 bg-slate-50 m-2 md:m-4 rounded-2xl md:rounded-3xl">
                <button onClick={() => { setViewMode('BOOK'); setStep(1); handleResetPackage(); }} className={`flex-1 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${viewMode === 'BOOK' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400'}`}>Platz buchen</button>
                <button onClick={() => { setViewMode('MY_BOOKINGS'); setStep(1); }} className={`flex-1 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${viewMode === 'MY_BOOKINGS' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400'}`}>Meine Termine</button>
            </div>

            <div className="p-4 md:p-10 pt-4 md:pt-4">
                {viewMode === 'BOOK' && (
                    <>
                        {step < 3 ? (
                            <div className="space-y-10">
                                {config.packages && config.packages.length > 0 && (
                                    <div className="bg-slate-50 border border-slate-100 rounded-2xl md:rounded-[32px] p-4 md:p-8 animate-fade-in">
                                        <button 
                                            onClick={() => setIsPackagesExpanded(!isPackagesExpanded)}
                                            className="w-full flex items-center justify-between group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black transition-colors ${selectedPackageId ? 'bg-[var(--color-secondary)] text-white' : 'bg-amber-100 text-amber-600'}`} style={{ backgroundColor: selectedPackageId ? secondaryColor : undefined }}>
                                                    <Gift size={16} />
                                                </div>
                                                <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Exklusive Specials & Pakete</h3>
                                                {selectedPackageId && (
                                                    <span className="text-[10px] font-black uppercase bg-[var(--color-secondary)]/10 px-2 py-0.5 rounded-full" style={{ color: secondaryColor }}>Aktiv</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4">
                                                {selectedPackageId && (
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleResetPackage(); }}
                                                        className="text-[10px] font-black text-slate-400 hover:text-red-500 uppercase tracking-widest transition-colors flex items-center gap-1"
                                                    >
                                                        <X size={12} /> Auswahl aufheben
                                                    </button>
                                                )}
                                                {isPackagesExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                                            </div>
                                        </button>

                                        {isPackagesExpanded && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in mt-6">
                                                {config.packages.map(pkg => {
                                                    const pkgCategory = categories.find(c => c.id === pkg.categoryId);
                                                    return (
                                                        <div key={pkg.id} className="relative group">
                                                            <button
                                                                onClick={() => handlePackageSelect(pkg)}
                                                                className={`w-full relative p-5 rounded-[24px] border-2 transition-all flex flex-col text-left group overflow-hidden ${selectedPackageId === pkg.id ? 'bg-[var(--color-secondary)]/5 shadow-lg' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                                                                style={{ borderColor: selectedPackageId === pkg.id ? secondaryColor : undefined }}
                                                            >
                                                                <div 
                                                                    className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl mb-3 transition-transform ${selectedPackageId === pkg.id ? 'text-white scale-110' : 'bg-slate-50 text-slate-600 group-hover:scale-110 group-hover:bg-slate-100'}`}
                                                                    style={{ backgroundColor: selectedPackageId === pkg.id ? secondaryColor : undefined }}
                                                                >
                                                                    {pkg.icon}
                                                                </div>
                                                                <div className={`text-xs font-black uppercase tracking-tight mb-1 ${selectedPackageId === pkg.id ? '' : 'text-slate-800'}`} style={{ color: selectedPackageId === pkg.id ? secondaryColor : undefined }}>{pkg.name}</div>
                                                                <div className="text-[10px] font-bold text-slate-400 flex flex-col gap-0.5">
                                                                    <span className="flex items-center gap-1"><LayoutGrid size={10} /> {pkgCategory?.name || 'Sportart'}</span>
                                                                    <span className="flex items-center gap-1"><Timer size={10} /> {pkg.durationMinutes} Min.</span>
                                                                    <span className="flex items-center gap-1"><Users size={10} /> {pkg.defaultPartySize} Pers.</span>
                                                                </div>
                                                                {selectedPackageId === pkg.id && (
                                                                    <div className="absolute top-4 right-4" style={{ color: secondaryColor }}>
                                                                        <CheckCircle size={20} />
                                                                    </div>
                                                                )}
                                                            </button>
                                                            {pkg.extendedInfo && (
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); setShowPackageInfoId(pkg.id); }}
                                                                    className="absolute top-3 right-3 w-6 h-6 bg-slate-100 hover:bg-emerald-100 text-slate-400 hover:text-emerald-600 rounded-full flex items-center justify-center transition-all shadow-sm z-20 group-hover:scale-110"
                                                                    title="Paket Details ansehen"
                                                                >
                                                                    <Info size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {availableCategories.length > 0 && (
                                    <>
                                        {!selectedPackageId && availableCategories.length > 1 && (
                                            <div className="animate-fade-in">
                                                <div className="flex items-center gap-3 mb-6">
                                                    <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center font-black">1</div>
                                                    <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Sportart wählen</h3>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    {availableCategories.map(cat => (
                                                        <button 
                                                            key={cat.id} 
                                                            onClick={() => {
                                                            setFormData({
                                                                ...formData, 
                                                                categoryId: cat.id, 
                                                                duration: Math.max(formData.duration, cat.minDuration || 60)
                                                            });
                                                            }}
                                                            className={`p-4 md:p-6 rounded-2xl md:rounded-[32px] border-2 transition-all flex flex-col items-center gap-3 group relative overflow-hidden ${formData.categoryId === cat.id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-50 bg-slate-50/50 hover:border-slate-200'}`}
                                                        >
                                                            <div className={`p-3 rounded-2xl transition-all ${formData.categoryId === cat.id ? 'bg-emerald-500 text-white rotate-6' : 'bg-white text-slate-400 group-hover:scale-110'}`}>{getCategoryIcon(cat.icon)}</div>
                                                            <span className={`text-xs font-black uppercase ${formData.categoryId === cat.id ? 'text-emerald-700' : 'text-slate-500'}`}>{cat.name}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className={`rounded-2xl md:rounded-[32px] p-5 md:p-8 text-white relative overflow-hidden transition-all duration-500 ${selectedPackageId ? 'bg-slate-800' : 'bg-slate-900'}`} style={{ backgroundColor: selectedPackageId ? `${secondaryColor}dd` : undefined }}>
                                            <div className="flex items-center gap-3 mb-8 relative z-10">
                                                <div className={`w-8 h-8 text-white rounded-xl flex items-center justify-center font-black ${selectedPackageId ? '' : 'bg-white/10'}`} style={{ backgroundColor: selectedPackageId ? 'rgba(255,255,255,0.2)' : undefined }}>
                                                    {selectedPackageId ? <Sparkles size={16} /> : (availableCategories.length === 1 ? '1' : '2')}
                                                </div>
                                                <h3 className="text-xs font-black uppercase tracking-widest">Details festlegen</h3>
                                                {selectedPackageId && (
                                                    <span className="ml-auto text-[9px] font-black uppercase bg-white/20 text-white px-2 py-1 rounded-full animate-pulse">Paket-Konfiguration</span>
                                                )}
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 md:gap-y-10 relative z-10 mb-8">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><Calendar size={12} className="text-white"/> Startdatum</label>
                                                    <input type="date" className="w-full p-4 bg-white/5 border border-white/10 rounded-2xl outline-none focus:border-white/40 transition-all text-sm font-bold text-white" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><Clock size={12}/> Gewählter Zeitraum</label>
                                                    <div className={`w-full p-4 rounded-2xl border flex items-center justify-center font-black text-lg transition-all ${calculatedTimeRange ? 'bg-white/10 border-white/30 text-white' : 'bg-white/5 border-white/10 text-slate-500'}`}>
                                                        {calculatedTimeRange || '--:--'}
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><Timer size={12}/> Spieldauer (Minuten)</label>
                                                    <div className={`flex items-center bg-white/5 border border-white/10 rounded-2xl overflow-hidden p-1 ${selectedPackageId ? 'opacity-60' : ''}`}>
                                                        <button 
                                                          disabled={!!selectedPackageId || formData.duration <= (selectedCategory?.minDuration || 15)} 
                                                          onClick={() => setFormData({...formData, duration: Math.max(selectedCategory?.minDuration || 15, formData.duration - 15)})} 
                                                          className="p-3 hover:bg-white/10 rounded-xl transition-colors disabled:opacity-30"
                                                        >
                                                          <Minus size={18}/>
                                                        </button>
                                                        <span className="flex-1 text-center font-black text-lg">{formData.duration} Min.</span>
                                                        <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, duration: formData.duration + 15})} className="p-3 hover:bg-white/10 rounded-xl transition-colors"><Plus size={18}/></button>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><Users size={12}/> Teamgröße</label>
                                                    <div className={`flex items-center bg-white/5 border border-white/10 rounded-2xl overflow-hidden p-1 ${selectedPackageId ? 'opacity-60' : ''}`}>
                                                        <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, partySize: Math.max(1, formData.partySize - 1)})} className="px-3 py-3 hover:bg-white/10 rounded-xl transition-colors"><Minus size={14} /></button>
                                                        <span className="flex-1 text-center font-black text-lg">{formData.partySize} Personen</span>
                                                        <button disabled={!!selectedPackageId} onClick={() => setFormData({...formData, partySize: formData.partySize + 1})} className="px-3 py-3 hover:bg-white/10 rounded-xl transition-colors"><Plus size={14} /></button>
                                                    </div>
                                                </div>
                                            </div>

                                            {(!currentUser && isGuestMode) && (
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 pt-8 border-t border-white/10 relative z-10">
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><UserIcon size={12}/> Name*</label>
                                                        <input 
                                                          required 
                                                          type="text" 
                                                          className={`w-full p-4 bg-white/5 border rounded-2xl outline-none focus:border-white/40 transition-all text-sm font-bold text-white ${formErrors.name ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)] ring-1 ring-red-500/20' : 'border-white/10'}`} 
                                                          value={formData.name} 
                                                          onChange={e => {
                                                            setFormData({...formData, name: e.target.value});
                                                            if (formErrors.name) setFormErrors({...formErrors, name: false});
                                                          }} 
                                                          placeholder="Vollständiger Name" 
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><Mail size={12}/> Email*</label>
                                                        <input 
                                                          required 
                                                          type="email" 
                                                          className={`w-full p-4 bg-white/5 border rounded-2xl outline-none focus:border-white/40 transition-all text-sm font-bold text-white ${formErrors.email ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)] ring-1 ring-red-500/20' : 'border-white/10'}`} 
                                                          value={formData.email} 
                                                          onChange={e => {
                                                            setFormData({...formData, email: e.target.value});
                                                            if (formErrors.email) setFormErrors({...formErrors, email: false});
                                                          }} 
                                                          placeholder="beispiel@mail.de" 
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70"><Phone size={12}/> Mobilnummer*</label>
                                                        <input 
                                                          required 
                                                          type="tel" 
                                                          className={`w-full p-4 bg-white/5 border rounded-2xl outline-none focus:border-white/40 transition-all text-sm font-bold text-white ${formErrors.phone ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)] ring-1 ring-red-500/20' : 'border-white/10'}`} 
                                                          value={formData.phone} 
                                                          onChange={e => {
                                                            setFormData({...formData, phone: e.target.value});
                                                            if (formErrors.phone) setFormErrors({...formErrors, phone: false});
                                                          }} 
                                                          placeholder="0170 1234567" 
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-8 relative z-10">
                                                <div className="flex items-center justify-between mb-4">
                                                    <label className="text-[10px] font-black text-slate-300 uppercase ml-2 flex items-center gap-2 opacity-70">
                                                        Startzeiten <span className="w-1 h-1 bg-white/30 rounded-full"></span> {formData.startTime || 'Keine'} gewählt
                                                    </label>
                                                    <div className="flex gap-4">
                                                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-white"></div> <span className="text-[9px] uppercase font-bold text-white/70">Frei</span></div>
                                                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-white/10 rounded-full"></div> <span className="text-[9px] uppercase font-bold text-white/30">Belegt</span></div>
                                                    </div>
                                                </div>
                                                
                                                {timeSlots.length === 0 ? (
                                                    <div className="p-8 bg-white/5 border border-white/5 rounded-2xl text-center">
                                                        <AlertCircle size={20} className="mx-auto text-white/50 mb-2" />
                                                        <p className="text-xs font-bold text-white/40">Keine freien Plätze in diesem Zeitfenster.</p>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                                                        {timeSlots.map(slot => (
                                                            <button
                                                                key={slot.time}
                                                                disabled={!slot.isAvailable}
                                                                onClick={() => setFormData({...formData, startTime: slot.time})}
                                                                className={`
                                                                    py-3 rounded-xl text-xs font-black transition-all border
                                                                    ${slot.time === formData.startTime 
                                                                        ? 'bg-white text-slate-900 border-white shadow-lg scale-105 z-20'
                                                                        : slot.isAvailable 
                                                                            ? 'bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/30' 
                                                                            : 'bg-white/5 border-transparent text-white/20 cursor-not-allowed opacity-30'}
                                                                `}
                                                            >
                                                                {slot.time}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <p className="mt-4 text-[9px] text-white/40 italic">Die Verfügbarkeit bezieht sich auf das gewählte Startdatum.</p>
                                            </div>

                                            {config.allowRecurringBookings && (
                                                <div className="mt-8 pt-8 border-t border-white/10 relative z-10">
                                                    <button 
                                                        onClick={() => setIsRecurring(!isRecurring)}
                                                        className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all w-full md:w-auto ${isRecurring ? 'bg-emerald-50/20 border-emerald-500' : 'bg-white/5 border-white/10 hover:border-white/30'}`}
                                                    >
                                                        <Repeat size={18} className={isRecurring ? 'text-emerald-400' : 'text-slate-400'} />
                                                        <div className="text-left">
                                                            <div className="text-xs font-black uppercase tracking-widest">Wöchentliche Serie</div>
                                                            <div className="text-[10px] opacity-60">Jeden {new Date(formData.date).toLocaleDateString('de-DE', { weekday: 'long' })} buchen</div>
                                                        </div>
                                                        <div className={`ml-auto w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isRecurring ? 'bg-emerald-50 border-emerald-500' : 'border-white/20'}`}>
                                                            {isRecurring && <CheckCircle size={14} />}
                                                        </div>
                                                    </button>
                                                    
                                                    {isRecurring && (
                                                        <div className="mt-4 p-5 bg-white/5 rounded-2xl border border-white/10 space-y-4 animate-fade-in max-w-sm">
                                                            <div>
                                                                <label className="text-[10px] font-black text-slate-300 uppercase ml-1 flex items-center gap-2 opacity-70">Letzter Termin der Serie</label>
                                                                <input 
                                                                    type="date" 
                                                                    className="w-full mt-2 p-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-emerald-400 text-sm font-bold text-white"
                                                                    min={formData.date}
                                                                    max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]}
                                                                    value={recurringEndDate}
                                                                    onChange={e => setRecurringEndDate(e.target.value)}
                                                                />
                                                                <p className="text-[9px] text-white/40 mt-1 italic">Maximal 1 Jahr im Voraus möglich.</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className={`rounded-2xl md:rounded-[32px] p-5 md:p-8 text-white relative overflow-hidden transition-all duration-500 ${selectedPackageId ? 'bg-slate-800' : 'bg-slate-900'}`} style={{ backgroundColor: selectedPackageId ? `${secondaryColor}cc` : undefined }}>
                                            <div className="flex items-center gap-3 mb-6 relative z-10">
                                                <div className={`w-8 h-8 text-white rounded-xl flex items-center justify-center font-black ${selectedPackageId ? '' : 'bg-white/10'}`} style={{ backgroundColor: selectedPackageId ? 'rgba(255,255,255,0.2)' : undefined }}>
                                                    {selectedPackageId ? <MessageSquare size={16} /> : (availableCategories.length === 1 ? '2' : '3')}
                                                </div>
                                                <h3 className="text-xs font-black uppercase tracking-widest">Notizen (Optional)</h3>
                                            </div>
                                            <div className="relative z-10">
                                                <textarea 
                                                    value={formData.notes}
                                                    onChange={e => setFormData({...formData, notes: e.target.value})}
                                                    placeholder="Z.B. Leihschläger, Turnier-Modus, etc."
                                                    className="w-full h-24 bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-white/30 outline-none focus:border-white/40 transition-all text-sm font-medium resize-none"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {Object.keys(formErrors).length > 0 && (
                                              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-500 rounded-2xl text-red-700 text-xs font-black uppercase tracking-widest animate-shake">
                                                <AlertCircle size={18} className="text-red-600" />
                                                <span>Bitte füllen Sie alle markierten Pflichtfelder aus, um fortzufahren.</span>
                                              </div>
                                            )}

                                            <button 
                                                onClick={handleDirectBooking}
                                                disabled={isProcessing || !formData.startTime || (isRecurring && !recurringEndDate)}
                                                className={`w-full py-6 text-white font-black text-lg rounded-[28px] shadow-2xl transition-all disabled:opacity-50 disabled:grayscale disabled:translate-y-0 hover:-translate-y-1`}
                                                style={{ backgroundColor: selectedPackageId ? secondaryColor : primaryColor }}
                                            >
                                                {!formData.startTime 
                                                  ? 'BITTE STARTZEIT WÄHLEN' 
                                                  : (isProcessing 
                                                      ? 'Verarbeite...' 
                                                      : (isRecurring 
                                                          ? (config.recurringRequireApproval ? 'SERIE KOSTENPFLICHTIG ANFRAGEN' : 'SERIE KOSTENPFLICHTIG RESERVIEREN')
                                                          : (config.requireReservationApproval ? 'JETZT RESERVIERUNG ANFRAGEN' : 'JETZT VERBINDLICH RESERVIEREN')))}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-16 animate-fade-in">
                                <div className={`w-24 h-24 rounded-[40px] flex items-center justify-center mx-auto mb-8 shadow-inner rotate-3 transition-colors ${(config.requireReservationApproval || (isRecurring && config.recurringRequireApproval)) ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                    {(config.requireReservationApproval || (isRecurring && config.recurringRequireApproval)) ? <Clock size={56} /> : <CheckCircle size={56} />}
                                </div>
                                <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tighter uppercase italic">
                                  {(config.requireReservationApproval || (isRecurring && config.recurringRequireApproval)) ? 'Anfrage erhalten!' : 'Game on!'}
                                </h2>
                                
                                <div className="text-slate-500 mb-8 max-w-lg mx-auto font-medium text-lg leading-relaxed">
                                    {isRecurring ? (
                                        <div className="space-y-4">
                                            <div className="bg-emerald-50 p-6 rounded-3xl border border-emerald-100 text-emerald-900 font-bold">
                                                {bookingResults?.success ?? 0} von {(bookingResults?.success ?? 0) + (bookingResults?.failed?.length ?? 0)} Terminen wurden erfolgreich geplant.
                                            </div>
                                            {bookingResults && bookingResults.failed.length > 0 && (
                                                <div className="bg-red-50 p-6 rounded-3xl border border-red-100 text-red-700 text-left">
                                                    <div className="text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-2"><AlertCircle size={14} /> Folgende Daten waren bereits belegt:</div>
                                                    <ul className="text-sm font-black grid grid-cols-2 gap-2">
                                                        {bookingResults.failed.map(d => <li key={d}>• {d}</li>)}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p>Dein Platz ist {config.requireReservationApproval ? 'angefragt' : 'gebucht'} für {formData.duration} Minuten am {new Date(formData.date).toLocaleDateString('de-DE')} um {formData.startTime} Uhr.</p>
                                    )}
                                </div>

                                <button onClick={() => {setStep(1); setViewMode('MY_BOOKINGS'); handleResetPackage();}} className="px-12 py-4 theme-bg-secondary text-white font-black rounded-2xl hover:opacity-90 transition-all flex items-center gap-3 mx-auto uppercase tracking-widest text-sm">
                                    ZUR ÜBERSICHT <ChevronRight size={20} />
                                </button>
                            </div>
                        )}
                    </>
                )}

                {viewMode === 'MY_BOOKINGS' && (
                    <div className="space-y-12">
                        {groupedUserReservations.pending.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-xs font-black text-amber-600 uppercase tracking-[0.2em] flex items-center gap-2 ml-4">
                                    <Clock size={14} /> Offene Anfragen
                                </h3>
                                {groupedUserReservations.pending.map(res => (
                                    <div key={res.id} className="p-4 md:p-6 border border-amber-100 rounded-2xl md:rounded-[32px] flex flex-col md:flex-row md:items-center justify-between bg-amber-50/30 hover:shadow-xl hover:shadow-amber-50/50 transition-all group gap-4 md:gap-0">
                                        <div className="flex items-center gap-6">
                                            <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center font-black group-hover:rotate-6 transition-transform">
                                                {res.seriesId ? <Repeat size={24} /> : <Timer size={24} />}
                                            </div>
                                            <div>
                                                <div className="font-black text-lg text-slate-900 uppercase italic">
                                                    {new Date(res.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} • {res.time} Uhr
                                                </div>
                                                <div className="text-[10px] font-bold text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 uppercase tracking-wider">
                                                    <span className="flex items-center gap-1"><Target size={10} /> {categories.find(c => c.id === res.categoryId)?.name || 'Sport'}</span>
                                                    {res.seriesId && <span className="flex items-center gap-1 text-emerald-600"><Repeat size={10} /> Abo/Serie</span>}
                                                    <span className="flex items-center gap-1"><Users size={10} /> {res.partySize} Spieler</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="px-4 py-1.5 bg-amber-100 text-amber-700 rounded-full font-black text-[10px] uppercase tracking-tighter">Warte auf Bestätigung</div>
                                            <button onClick={() => { if(window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) onCancelReservation(res.id); }} className="p-2 text-slate-300 hover:text-red-500 transition-colors" title="Anfrage löschen">
                                                <X size={20} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="space-y-4">
                            <h3 className="text-xs font-black text-emerald-600 uppercase tracking-[0.2em] flex items-center gap-2 ml-4">
                                <Calendar size={14} /> Anstehende Termine
                            </h3>
                            {groupedUserReservations.upcoming.length === 0 ? (
                                <div className="text-center py-10 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-200">
                                    <Hourglass size={40} className="mx-auto text-slate-300 mb-4" />
                                    <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Keine anstehenden Buchungen</p>
                                </div>
                            ) : (
                                groupedUserReservations.upcoming.map(res => (
                                    <div key={res.id} className="p-4 md:p-6 border border-slate-100 rounded-2xl md:rounded-[32px] flex flex-col md:flex-row md:items-center justify-between bg-white hover:shadow-xl hover:shadow-slate-100 transition-all group gap-4 md:gap-0">
                                        <div className="flex items-center gap-6">
                                            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center font-black group-hover:rotate-6 transition-transform">
                                                {res.seriesId ? <Repeat size={24} /> : <CheckCircle size={24} />}
                                            </div>
                                            <div>
                                                <div className="font-black text-lg text-slate-900 uppercase italic">
                                                    {new Date(res.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} • {res.time} Uhr
                                                </div>
                                                <div className="text-[10px] font-bold text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 uppercase tracking-wider">
                                                    <span className="flex items-center gap-1"><Target size={10} /> {categories.find(c => c.id === res.categoryId)?.name || 'Sport'}</span>
                                                    {res.seriesId && <span className="flex items-center gap-1 text-emerald-600"><Repeat size={10} /> Abo/Serie</span>}
                                                    <span className="flex items-center gap-1"><Users size={10} /> {res.partySize} Spieler</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="px-4 py-1.5 bg-emerald-50 text-emerald-700 rounded-full font-black text-[10px] uppercase tracking-tighter">Bestätigt</div>
                                            <div className="flex gap-1">
                                                <button onClick={() => handleEditReservation(res)} className="p-2 text-slate-300 hover:text-blue-500 transition-colors" title="Termin ändern">
                                                    <Pencil size={20} />
                                                </button>
                                                <button onClick={() => { if(window.confirm('Möchten Sie diesen Termin wirklich absagen?')) onCancelReservation(res.id); }} className="p-2 text-slate-300 hover:text-red-500 transition-colors" title="Termin absagen">
                                                    <Trash2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {groupedUserReservations.past.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2 ml-4">
                                    <RotateCcw size={14} /> Vergangene Spiele
                                </h3>
                                <div className="bg-slate-50 border border-slate-100 rounded-2xl md:rounded-[40px] p-4 md:p-6 space-y-3">
                                    {groupedUserReservations.past.map(res => (
                                        <div key={res.id} className="p-4 border-b border-slate-200/50 last:border-0 flex items-center justify-between opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all">
                                            <div className="flex items-center gap-4">
                                                <div className="text-xs font-black text-slate-500 bg-white w-10 h-10 rounded-xl flex items-center justify-center shadow-sm border border-slate-100 uppercase">
                                                    {new Date(res.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-sm text-slate-700 uppercase">{res.time} Uhr • {categories.find(c => c.id === res.categoryId)?.name || 'Sport'}</div>
                                                    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                                        <span>{res.durationMinutes} Min.</span>
                                                        <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
                                                        <span>{res.partySize} Spieler</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button onClick={() => {
                                                setFormData({...formData, categoryId: res.categoryId, partySize: res.partySize, duration: res.durationMinutes});
                                                setViewMode('BOOK');
                                                setStep(1);
                                            }} className="p-2 text-slate-300 hover:text-emerald-500 transition-colors" title="Nochmal buchen">
                                                <Plus size={18} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};